<!-- Loading State -->
<div class="state-screen loading-screen" *ngIf="loading">
  <div class="state-card">
    <div class="spinner" aria-hidden="true"></div>
    <p>Fetching your subscription details…</p>
  </div>
</div>

<!-- Error State -->
<div class="state-screen error-screen" *ngIf="!loading && errorMessage">
  <div class="state-card">
    <div class="state-icon error-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="13"></line>
        <line x1="12" y1="16.5" x2="12.01" y2="16.5"></line>
      </svg>
    </div>
    <h2>We couldn't load your details</h2>
    <p>{{ errorMessage }}</p>
  </div>
</div>

<!-- Payment Failed Screen -->
<div class="state-screen error-screen"
  *ngIf="!loading && !errorMessage && paymentOutcome === 'FAILED'">
  <div class="state-card">
    <div class="state-icon error-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
    </div>
    <h2>Payment unsuccessful</h2>
    <p>We couldn't complete your payment, so your subscription wasn't activated.</p>
    <p class="failure-reason" *ngIf="failureReason"><strong>Reason:</strong> {{ failureReason }}</p>
    <p>Don't worry — you can pick up right where you left off and try again. If any
      amount was deducted, it will be refunded automatically.</p>
    <div class="state-actions no-print">
      <button type="button" class="btn-primary" (click)="retryPayment()">
        Retry payment
      </button>
    </div>
  </div>
</div>

<!-- Payment Pending / Confirming Screen -->
<div class="state-screen loading-screen"
  *ngIf="!loading && !errorMessage && paymentOutcome === 'PENDING'">
  <div class="state-card">
    <div class="spinner" aria-hidden="true"></div>
    <h2>Confirming your payment…</h2>
    <p>This usually takes a few seconds. We're waiting for your bank to confirm the
      transaction — you'll receive an email as soon as it's done.</p>
    <div class="state-actions no-print">
      <button type="button" class="btn-secondary-outline" (click)="checkAgain()">
        Check again
      </button>
    </div>
  </div>
</div>

<!-- Success Screen -->
<div class="success-page"
  *ngIf="!loading && !errorMessage && paymentOutcome === 'SUCCESS' && subscriptionInfo">
  <div class="success-card">

    <!-- Brand bar -->
    <div class="brand-bar">
      <img src="assets/images/DAAI_FULL_Logo.png" alt="DAAI" class="brand-logo" />
    </div>

    <!-- Hero -->
    <header class="success-hero">
      <div class="check-badge" aria-hidden="true">
        <svg viewBox="0 0 52 52">
          <circle class="check-circle" cx="26" cy="26" r="24" fill="none" />
          <path class="check-mark" fill="none" d="M14 27 l8 8 l16 -18" />
        </svg>
      </div>
      <h1>You're all set!</h1>
      <p class="hero-subtitle">
        Your subscription is active and ready to go.
      </p>
      <p class="hero-email">
        We've emailed your account details to
        <strong>{{ subscriptionInfo.userDetails.email }}</strong>
      </p>
    </header>

    <!-- Summary grid -->
    <section class="summary-grid">
      <!-- Subscription -->
      <article class="summary-card">
        <div class="summary-card-header">
          <span class="summary-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="16" rx="2"></rect>
              <path d="M3 10h18"></path>
              <path d="M8 2v4M16 2v4"></path>
            </svg>
          </span>
          <h2>Subscription</h2>
        </div>
        <dl class="kv-list">
          <div class="kv-row">
            <dt>Subscription ID</dt>
            <dd class="mono">{{ subscriptionInfo.ds_subscriptionId }}</dd>
          </div>
          <div class="kv-row" *ngIf="subscriptionInfo.tenantDetails?._id">
            <dt>Tenant ID</dt>
            <dd class="mono">{{ subscriptionInfo.tenantDetails._id }}</dd>
          </div>
          <div class="kv-row">
            <dt>Activated</dt>
            <dd>{{ subscriptionInfo.createdDate | date : "mediumDate" }}</dd>
          </div>
          <div class="kv-row">
            <dt>Status</dt>
            <dd>
              <span class="status-pill"
                [class.status-active]="subscriptionInfo.status === 'ACTIVE'">
                {{ subscriptionInfo.status }}
              </span>
            </dd>
          </div>
        </dl>
      </article>

      <!-- Plan -->
      <article class="summary-card">
        <div class="summary-card-header">
          <span class="summary-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2 L3 7 v6 c0 5 4 8 9 9 5 -1 9 -4 9 -9 V7 Z"></path>
              <path d="M9 12 l2 2 l4 -4"></path>
            </svg>
          </span>
          <h2>Plan</h2>
        </div>
        <dl class="kv-list">
          <div class="kv-row">
            <dt>Name</dt>
            <dd class="value-strong">{{ subscriptionInfo.planDetails.name }}</dd>
          </div>
          <div class="kv-row">
            <dt>Billing</dt>
            <dd>{{ subscriptionInfo.planDetails.intervalType | titlecase }}</dd>
          </div>
        </dl>
      </article>

      <!-- Account -->
      <article class="summary-card">
        <div class="summary-card-header">
          <span class="summary-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </span>
          <h2>Account holder</h2>
        </div>
        <dl class="kv-list">
          <div class="kv-row">
            <dt>Name</dt>
            <dd>
              {{ subscriptionInfo.userDetails.personalDetails.firstname }}
              {{ subscriptionInfo.userDetails.personalDetails.lastname }}
            </dd>
          </div>
          <div class="kv-row">
            <dt>Email</dt>
            <dd class="value-break">{{ subscriptionInfo.userDetails.email }}</dd>
          </div>
          <div class="kv-row">
            <dt>Phone</dt>
            <dd>{{ subscriptionInfo.userDetails.personalDetails.mobileNumber }}</dd>
          </div>
        </dl>
      </article>
    </section>

    <!-- Payment summary -->
    <section class="payment-summary">
      <div class="payment-summary-title">Payment summary</div>
      <div class="payment-row">
        <span>Plan price</span>
        <span>₹{{ subscriptionInfo.planDetails.basePrice | number : "1.2-2" }}</span>
      </div>
      <div class="payment-row">
        <span>Tax</span>
        <span>
          ₹{{
            (subscriptionInfo.planDetails.totalAmountWithTax
              ? subscriptionInfo.planDetails.totalAmountWithTax -
                subscriptionInfo.planDetails.basePrice
              : 0
            ) | number : "1.2-2"
          }}
        </span>
      </div>
      <div class="payment-row payment-total">
        <span>Total paid</span>
        <span>
          ₹{{
            (subscriptionInfo.planDetails.totalAmountWithTax ||
              subscriptionInfo.planDetails.basePrice) | number : "1.2-2"
          }}
        </span>
      </div>
    </section>

    <!-- Next steps -->
    <section class="next-steps">
      <h3>What's next?</h3>
      <ol>
        <li>Check your inbox for your login credentials.</li>
        <li>Sign in to your DAAI workspace to start setting things up.</li>
        <li>Need a hand? Reply to your welcome email and our team will help.</li>
      </ol>
    </section>

    <!-- Actions -->
    <div class="success-actions no-print">
      <button type="button" class="btn-secondary-outline" (click)="printPage()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
          <polyline points="6 9 6 2 18 2 18 9"></polyline>
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
          <rect x="6" y="14" width="12" height="8"></rect>
        </svg>
        Print receipt
      </button>
    </div>

    <p class="thank-you-note">Thank you for choosing DAAI. We're glad to have you on board.</p>
  </div>
</div>
